/* ============================================================================
   Volume discount progress — cart-context + global bottom bar.

   Both progress surfaces start from Liquid SSR. Mutations prefer Liquid
   cart-state so selected collections are counted by Shopify's product
   collection membership. Only the true wildcard mode (no configured
   collections) uses the Ajax cart quantity directly.
   ========================================================================= */

(function () {
  'use strict';

  var CART_EVENTS = [
    'on:cart:add',
    'on:line-item:change',
    'cart:updated',
    'cart:change',
    'shopify:cart:updated',
    'volume-discount:cart-state',
    'volume-discount:discounted-subtotal',
    'dispatch:cart-drawer:refresh'
  ];
  var DEFAULT_CAMPAIGN_ID = 'volume_discount';
  var STATE_SECTION_ID = 'volume-discount-cart-state';
  var STATE_VERSION = '2';
  var IMPLEMENTATION_VERSION = 'v10-campaign-state';

  function safeNumber(value) {
    var n = Number(value);
    return isFinite(n) ? n : 0;
  }

  function cartJsonUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') + 'cart.js';
  }

  function cartStateUrl(sectionId, revision) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/?$/, '/') +
      '?section_id=' + encodeURIComponent(sectionId || STATE_SECTION_ID) +
      '&_volume_discount=' + encodeURIComponent(String(revision));
  }

  function optionalCount(value) {
    if (value === undefined || value === null || value === '') return null;
    var number = Number(value);
    if (!isFinite(number) || number < 0) return null;
    return Math.floor(number);
  }

  function optionalMoney(value) {
    if (value === undefined || value === null || value === '') return null;
    var number = Number(value);
    if (!isFinite(number) || number < 0) return null;
    return Math.round(number);
  }

  function allCartLinesEligibleFromNode(node) {
    return node.dataset.allCartLinesEligible === 'true' ||
      node.dataset.allProductsEligible === 'true';
  }

  function nodeCampaignId(node) {
    return node && node.dataset && node.dataset.campaignId
      ? node.dataset.campaignId
      : DEFAULT_CAMPAIGN_ID;
  }

  function nodeMatchesCampaign(node, campaignId) {
    return nodeCampaignId(node) === (campaignId || DEFAULT_CAMPAIGN_ID);
  }

  function parseCartStateNode(node, campaignId) {
    if (!node) return null;
    if (!nodeMatchesCampaign(node, campaignId)) return null;
    if (node.dataset.stateVersion !== STATE_VERSION) return null;
    var eligibleCount = optionalCount(node.dataset.eligibleCount);
    if (eligibleCount === null) return null;
    return {
      eligibleCount: eligibleCount,
      cartItemCount: optionalCount(node.dataset.cartItemCount),
      cartTotalPrice: optionalMoney(node.dataset.cartTotalPrice),
      freeShippingThreshold: optionalMoney(node.dataset.freeShippingThreshold),
      standardShippingRate: optionalMoney(node.dataset.standardShippingRate),
      currencyCode: node.dataset.currencyCode || '',
      allCartLinesEligible: allCartLinesEligibleFromNode(node)
    };
  }

  function parseCartStateHtml(html, campaignId) {
    if (typeof html !== 'string' || !html) return null;
    var template = document.createElement('template');
    template.innerHTML = html;
    var nodes = Array.from(template.content.querySelectorAll('[data-volume-discount-cart-state]'));
    for (var i = 0; i < nodes.length; i++) {
      var state = parseCartStateNode(nodes[i], campaignId);
      if (state) return state;
    }
    return null;
  }

  function parseProgressStateNode(node, campaignId) {
    if (!node) return null;
    if (!nodeMatchesCampaign(node, campaignId)) return null;
    var eligibleCount = optionalCount(node.dataset.initialCount);
    if (eligibleCount === null) return null;
    return {
      eligibleCount: eligibleCount,
      cartItemCount: null,
      cartTotalPrice: optionalMoney(node.dataset.cartTotalPrice),
      freeShippingThreshold: optionalMoney(node.dataset.freeShippingThreshold),
      standardShippingRate: optionalMoney(node.dataset.standardShippingRate),
      currencyCode: node.dataset.currencyCode || '',
      allCartLinesEligible: allCartLinesEligibleFromNode(node)
    };
  }

  function parseProgressStateHtml(html, campaignId) {
    if (typeof html !== 'string' || !html) return null;
    var template = document.createElement('template');
    template.innerHTML = html;
    var nodes = Array.from(
      template.content.querySelectorAll('.volume-discount-bar--cart, volume-discount-progress')
    );
    for (var i = 0; i < nodes.length; i++) {
      var state = parseProgressStateNode(nodes[i], campaignId);
      if (state) return state;
    }
    return null;
  }

  function cartStateFromSections(sections, sectionId, campaignId) {
    if (!sections || typeof sections !== 'object') return null;
    var stateSectionId = sectionId || STATE_SECTION_ID;
    var html = sections[stateSectionId] ||
      sections['shopify-section-' + stateSectionId];
    var state = parseCartStateHtml(html, campaignId);
    if (state) return state;

    var keys = Object.keys(sections);
    for (var i = 0; i < keys.length; i++) {
      state = parseCartStateHtml(sections[keys[i]], campaignId) ||
        parseProgressStateHtml(sections[keys[i]], campaignId);
      if (state) return state;
    }
    return null;
  }

  function fullCartQuantity(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    return cart.items.reduce(function (total, item) {
      return total + (Number(item.quantity) || 0);
    }, 0);
  }

  function parseJsonArray(value) {
    try {
      var parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (_) {
      return [];
    }
  }

  function parseCsvList(value) {
    return String(value || '')
      .split(',')
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function teaserPriority(teaser) {
    return safeNumber(teaser && teaser.dataset ? teaser.dataset.campaignPriority : 0);
  }

  function teaserMatchesVariant(teaser, variant) {
    if (!teaser) return false;
    if (teaser.dataset.requiresEligibleVariant !== 'true') return true;
    var variantId = variant && variant.id !== undefined && variant.id !== null
      ? String(variant.id)
      : String(teaser.dataset.currentVariantId || '');
    if (!variantId) return false;
    return parseCsvList(teaser.dataset.eligibleVariantIds).includes(variantId);
  }

  function syncVolumeDiscountTeaserGroup(anchor, variant) {
    if (!anchor || !anchor.dataset || !anchor.dataset.productId) return;
    var root = anchor.closest('.shopify-section') || document;
    var productId = anchor.dataset.productId;
    var teasers = Array.from(root.querySelectorAll('volume-discount-teaser[data-product-id]'))
      .filter(function (teaser) { return teaser.dataset.productId === productId; });
    if (!teasers.length) return;

    var activeTeaser = null;
    teasers.forEach(function (teaser) {
      if (!teaserMatchesVariant(teaser, variant)) return;
      if (!activeTeaser || teaserPriority(teaser) > teaserPriority(activeTeaser)) {
        activeTeaser = teaser;
      }
    });

    teasers.forEach(function (teaser) {
      teaser.hidden = teaser !== activeTeaser;
    });

    var wrapper = anchor.closest('.product-info__block--volume-discount-teaser');
    if (wrapper) wrapper.hidden = !activeTeaser;
  }

  function syncGlobalProgressSuppression(root, variant) {
    if (!root || !root.dataset) return;
    var progress = root.querySelector('volume-discount-progress');
    if (!progress || root.dataset.canShowDiscountBar !== 'true') return;

    var variantId = variant && variant.id !== undefined && variant.id !== null
      ? String(variant.id)
      : '';
    var suppressed = variantId &&
      parseCsvList(root.dataset.suppressProgressVariantIds).includes(variantId);

    progress.hidden = Boolean(suppressed);
    root.dataset.hasDiscountBar = String(!suppressed);
    root.dataset.showDiscountBar = String(!suppressed);
  }

  function setupGlobalProgressSuppression() {
    var roots = Array.from(document.querySelectorAll('[data-volume-discount-global][data-suppress-progress-variant-ids]'));
    if (!roots.length) return;

    roots.forEach(function (root) {
      if (root.dataset.volumeDiscountSuppressionReady === 'true') return;
      root.dataset.volumeDiscountSuppressionReady = 'true';

      var summary = root.querySelector('volume-discount-pdp-summary');
      syncGlobalProgressSuppression(root, {
        id: summary && summary.dataset ? summary.dataset.currentVariantId : ''
      });

      document.addEventListener('on:variant:change', function (event) {
        var detail = event && event.detail ? event.detail : {};
        if (!detail.variant) return;
        syncGlobalProgressSuppression(root, detail.variant);
      });
    });
  }

  function readTierConfig(element, index) {
    return {
      index: index,
      enabled: element.getAttribute('data-tier-' + index + '-enabled') === 'true',
      count: safeNumber(element.getAttribute('data-tier-' + index + '-count')),
      percent: safeNumber(element.getAttribute('data-tier-' + index + '-percent')),
      label: element.getAttribute('data-tier-' + index + '-label') || '',
      before: element.getAttribute('data-tier-' + index + '-before') || '',
      at: element.getAttribute('data-tier-' + index + '-at') || '',
      progress: element.getAttribute('data-tier-' + index + '-progress') || ''
    };
  }

  function tierPercentByIndex(tiers, index) {
    var tier = (tiers || []).find(function (item) {
      return item.index === index;
    });
    return tier ? String(tier.percent) : '';
  }

  function tierQuantityByIndex(tiers, index) {
    var tier = (tiers || []).find(function (item) {
      return item.index === index;
    });
    return tier ? String(tier.count) : '';
  }

  function replaceTierPlaceholders(template, tiers, replacements) {
    return String(template || '')
      .replace(/\[tier[\s_]*([1-4])[\s_]*(?:percent|percentage)\]/gi, function (_match, index) {
        return tierPercentByIndex(tiers, Number(index));
      })
      .replace(/\[tier[\s_]*([1-4])[\s_]*(?:count|quantity)\]/gi, function (_match, index) {
        return tierQuantityByIndex(tiers, Number(index));
      })
      .replace(/\[remaining\]/g, replacements && replacements.remaining !== undefined ? String(replacements.remaining) : '')
      .replace(/\[current_percent\]/g, replacements && replacements.currentPercent !== undefined ? String(replacements.currentPercent) : '')
      .replace(/\[next_percent\]/g, replacements && replacements.nextPercent !== undefined ? String(replacements.nextPercent) : '');
  }

  function hasUnresolvedPlaceholder(value) {
    return /\[[^\]]+\]/.test(String(value || ''));
  }

  function renderTierTemplate(template, fallback, tiers, replacements) {
    var message = replaceTierPlaceholders(template, tiers, replacements);
    if (hasUnresolvedPlaceholder(message) && fallback && fallback !== template) {
      message = replaceTierPlaceholders(fallback, tiers, replacements);
    }
    return message;
  }

  function evaluateTiers(count, tiers, templates) {
    var active = (tiers || [])
      .filter(function (t) { return t.enabled && t.count > 0; })
      .sort(function (a, b) { return a.count - b.count; });

    if (!active.length) return null;

    var currentIndex = -1;
    for (var i = 0; i < active.length; i++) {
      if (count >= active[i].count) currentIndex = i;
    }
    var current = currentIndex >= 0 ? active[currentIndex] : null;
    var next = currentIndex + 1 < active.length ? active[currentIndex + 1] : null;

    templates = templates || {};
    var tpl;
    if (current && next) {
      tpl = next.progress || templates.discountThenNext || '';
    } else if (current) {
      tpl = current.at || templates.discountUnlocked || '';
    } else {
      tpl = active[0].before || templates.unlockDiscount || '';
    }

    var remaining = next ? Math.max(0, next.count - count) : 0;
    var fallback = current && next
      ? templates.discountThenNext
      : (current ? templates.discountUnlocked : templates.unlockDiscount);
    var message = renderTierTemplate(tpl, fallback, tiers, {
      remaining: remaining,
      currentPercent: current ? current.percent : 0,
      nextPercent: next ? next.percent : ''
    });

    return {
      message: message,
      statusState: current && next ? 'progress' : (current ? 'unlocked' : 'before'),
      maxCount: active[active.length - 1].count
    };
  }

  function evaluateMixedProgress(count, cartTotalPrice, tiers, freeShippingThreshold, freeShippingNodeCount, templates) {
    var active = (tiers || [])
      .filter(function (t) { return t.enabled && t.count > 0; })
      .sort(function (a, b) { return a.count - b.count; });

    if (!active.length) return null;

    var maxCount = active[active.length - 1].count;
    var nodeCount = Math.max(0, safeNumber(freeShippingNodeCount) || 2.5);
    if (nodeCount >= active[0].count) nodeCount = Math.max(0.5, active[0].count - 0.5);
    var nodePct = Math.min(100, (nodeCount / maxCount) * 100);
    var threshold = Math.max(0, safeNumber(freeShippingThreshold));
    var subtotal = Math.max(0, safeNumber(cartTotalPrice));
    var freeShippingEnabled = threshold > 0;
    var freeShippingReached = !freeShippingEnabled || subtotal >= threshold;

    var currentIndex = -1;
    for (var i = 0; i < active.length; i++) {
      if (count >= active[i].count) currentIndex = i;
    }
    var current = currentIndex >= 0 ? active[currentIndex] : null;
    var next = currentIndex + 1 < active.length ? active[currentIndex + 1] : null;

    templates = templates || {};
    var message;
    var statusState;
    if (freeShippingEnabled && !freeShippingReached) {
      message = replaceTierPlaceholders(
        templates.addAmountFreeShipping || '',
        tiers,
        { amount: formatMoney(Math.max(0, threshold - subtotal)) }
      ).replace(/\[amount\]/g, formatMoney(Math.max(0, threshold - subtotal)));
      statusState = 'free-shipping';
    } else if (!current) {
      var first = active[0];
      var firstTpl = first.before || (freeShippingEnabled
        ? (templates.freeShippingThenDiscount || '')
        : (templates.unlockDiscount || ''));
      var firstFallback = freeShippingEnabled
        ? (templates.freeShippingThenDiscount || templates.unlockDiscount || '')
        : (templates.unlockDiscount || '');
      message = firstTpl
        ? renderTierTemplate(firstTpl, firstFallback, tiers, {
          remaining: Math.max(0, first.count - count),
          currentPercent: 0,
          nextPercent: first.percent
        })
        : '';
      statusState = 'progress';
    } else if (next) {
      var tpl = next.progress || templates.discountThenNext || '';
      message = renderTierTemplate(tpl, templates.discountThenNext || '', tiers, {
        remaining: Math.max(0, next.count - count),
        currentPercent: current.percent,
        nextPercent: next.percent
      });
      statusState = 'progress';
    } else {
      message = renderTierTemplate(
        current.at || templates.discountUnlocked || '',
        templates.discountUnlocked || '',
        tiers,
        {
          remaining: 0,
          currentPercent: current.percent,
          nextPercent: ''
        }
      );
      statusState = 'unlocked';
    }

    var pct = 0;
    if (freeShippingEnabled && !freeShippingReached) {
      pct = nodePct * Math.min(1, subtotal / threshold);
    } else {
      pct = Math.min(100, (count / maxCount) * 100);
      if (freeShippingEnabled) pct = Math.max(nodePct, pct);
    }

    return {
      message: message,
      statusState: statusState,
      currentDiscountPercent: current ? current.percent : 0,
      maxCount: maxCount,
      progressPct: pct,
      freeShippingReached: freeShippingReached,
      freeShippingNodePct: nodePct
    };
  }

  function splitProgressMessage(message) {
    var text = String(message || '');
    var delimiterIndex = text.indexOf('·');
    if (delimiterIndex !== -1) {
      return {
        lead: text.slice(0, delimiterIndex + 1).replace(/\s*$/, '') + ' ',
        emphasis: text.slice(delimiterIndex + 1).trim()
      };
    }
    var addMatch = /\badd\b/i.exec(text);
    if (addMatch && addMatch.index > 0) {
      return {
        lead: text.slice(0, addMatch.index),
        emphasis: text.slice(addMatch.index).trim()
      };
    }
    return { lead: '', emphasis: text };
  }

  class VolumeDiscountProgress extends HTMLElement {
    constructor() {
      super();
      this._connected = false;
      this._syncRevision = 0;
      this._lastSync = null;
    }

    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.dataset.volumeDiscountImplementation = IMPLEMENTATION_VERSION;
      this.campaignId = this.dataset.campaignId || DEFAULT_CAMPAIGN_ID;
      this.stateSectionId = this.dataset.stateSectionId || STATE_SECTION_ID;
      this.allCartLinesEligible = this.dataset.allCartLinesEligible === 'true' ||
        this.dataset.allProductsEligible === 'true';
      this.eligibleCollectionHandles = parseJsonArray(this.dataset.eligibleCollectionHandles)
        .filter(Boolean);

      this.tiers = [1, 2, 3, 4].map(function (i) {
        return readTierConfig(this, i);
      }, this);

      this.messageEl = this.querySelector('.volume-discount-bar__message');
      this.countEl = this.querySelector('[data-volume-discount-count]');
      this.milestones = Array.from(
        this.querySelectorAll('[data-volume-discount-milestone]')
      );
      this.freeShippingMilestone = this.querySelector('[data-volume-discount-free-shipping-milestone]');
      this.tierLabels = Array.from(this.querySelectorAll('[data-volume-discount-label]'));
      this.freeShippingLabel = this.querySelector('[data-volume-discount-free-shipping-label]');
      this.freeShippingThreshold = optionalMoney(this.dataset.freeShippingThreshold) || 0;
      this.standardShippingRate = optionalMoney(this.dataset.standardShippingRate) || 0;
      this.freeShippingNodeCount = safeNumber(this.dataset.freeShippingNodeCount) || 2.5;
      this.cartTotalPrice = optionalMoney(this.dataset.cartTotalPrice) || 0;
      this.toggleButton = this.querySelector('[data-volume-discount-toggle]');

      this._lastSync = {
        source: 'ssr',
        eligibleCount: safeNumber(this.dataset.initialCount),
        cartTotalPrice: this.cartTotalPrice,
        cartItemCount: null,
        applied: true,
        appliedAt: Date.now()
      };

      this.onCartEvent = (event) => {
        this.syncFromCartEvent(event);
      };
      this.onToggleClick = () => this.toggleGlobalExpansion();

      CART_EVENTS.forEach((name) => {
        document.addEventListener(name, this.onCartEvent);
      });
      if (this.toggleButton) {
        this.toggleButton.addEventListener('click', this.onToggleClick);
      }

      this.refresh('initial');
    }

    disconnectedCallback() {
      if (this.onCartEvent) {
        CART_EVENTS.forEach((name) => {
          document.removeEventListener(name, this.onCartEvent);
        });
      }
      if (this.toggleButton && this.onToggleClick) {
        this.toggleButton.removeEventListener('click', this.onToggleClick);
      }
      this._connected = false;
      this._syncRevision += 1;
    }

    toggleGlobalExpansion() {
      var expanded = !this.classList.contains('is-teaser-expanded');
      this.classList.toggle('is-teaser-expanded', expanded);
      if (this.toggleButton) {
        this.toggleButton.setAttribute('aria-expanded', String(expanded));
        this.toggleButton.setAttribute(
          'aria-label',
          expanded ? this.dataset.hideProgressLabel : this.dataset.showProgressLabel
        );
      }
    }

    syncFromCartEvent(event) {
      var revision = ++this._syncRevision;
      var detail = event && event.detail ? event.detail : {};
      var source = 'cart-event:' + (event && event.type ? event.type : 'unknown');

      var discountedSubtotal = optionalMoney(detail.totalDiscountedCents);
      if (discountedSubtotal !== null) {
        return Promise.resolve(
          this._applyCount(
            safeNumber(this.dataset.lastEligibleCount || this.dataset.initialCount),
            {
              source: source + ':discounted-subtotal',
              cartItemCount: optionalCount(this.dataset.lastCartItemCount),
              cartTotalPrice: discountedSubtotal
            },
            revision
          )
        );
      }

      var serverState = cartStateFromSections(detail.sections, this.stateSectionId, this.campaignId);
      if (serverState) {
        return Promise.resolve(
          this._applyServerState(serverState, source + ':section', revision)
        );
      }

      if (this.allCartLinesEligible && detail.cart && Array.isArray(detail.cart.items)) {
        return this.renderAllCart(detail.cart, source + ':cart', revision);
      }

      return this._refreshForRevision(revision, source + ':fallback');
    }

    refresh(source) {
      var revision = ++this._syncRevision;
      return this._refreshForRevision(revision, source || 'manual');
    }

    async _refreshForRevision(revision, source) {
      try {
        if (this.allCartLinesEligible) {
          var cartResponse = await fetch(cartJsonUrl(), {
            cache: 'no-store',
            headers: { Accept: 'application/json' }
          });
          if (cartResponse.ok) {
            return this.renderAllCart(await cartResponse.json(), source + ':cart', revision);
          }
        }

        var stateResponse = await fetch(cartStateUrl(this.stateSectionId, revision), {
          cache: 'no-store',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        if (!stateResponse.ok) return false;
        var serverState = parseCartStateHtml(await stateResponse.text(), this.campaignId);
        if (!serverState) return false;
        return this._applyServerState(serverState, source + ':section', revision);
      } catch (_) {
        return false;
      }
    }

    renderAllCart(cart, source, revision) {
      if (!cart || !Array.isArray(cart.items)) return false;
      if (revision !== this._syncRevision || !this.isConnected) return false;

      var quantity = fullCartQuantity(cart);
      if (quantity === null) return false;
      return this._applyCount(quantity, {
        source: source,
        cartItemCount: optionalCount(cart.item_count),
        cartTotalPrice: optionalMoney(cart.total_price)
      }, revision);
    }

    _applyServerState(serverState, source, revision) {
      this.allCartLinesEligible = serverState.allCartLinesEligible;
      this.dataset.allCartLinesEligible = String(serverState.allCartLinesEligible);
      delete this.dataset.allProductsEligible;
      if (serverState.freeShippingThreshold !== null) {
        this.freeShippingThreshold = serverState.freeShippingThreshold;
        this.dataset.freeShippingThreshold = String(serverState.freeShippingThreshold);
      }
      if (serverState.standardShippingRate !== null) {
        this.standardShippingRate = serverState.standardShippingRate;
        this.dataset.standardShippingRate = String(serverState.standardShippingRate);
      }
      if (serverState.currencyCode) this.dataset.currencyCode = serverState.currencyCode;
      return this._applyCount(serverState.eligibleCount, {
        source: source,
        cartItemCount: serverState.cartItemCount,
        cartTotalPrice: serverState.cartTotalPrice
      }, revision);
    }

    _applyCount(count, metadata, revision) {
      if (revision !== this._syncRevision || !this.isConnected) return false;
      var cartTotalPrice = metadata.cartTotalPrice !== null && metadata.cartTotalPrice !== undefined
        ? metadata.cartTotalPrice
        : this.cartTotalPrice;
      this.renderCount(count, cartTotalPrice);
      this.cartTotalPrice = Math.max(0, safeNumber(cartTotalPrice));
      this._lastSync = {
        source: metadata.source,
        eligibleCount: count,
        cartItemCount: metadata.cartItemCount,
        cartTotalPrice: this.cartTotalPrice,
        applied: true,
        appliedAt: Date.now(),
        revision: revision
      };
      this.dataset.lastSyncSource = metadata.source;
      this.dataset.lastEligibleCount = String(count);
      this.dataset.lastCartTotalPrice = String(this.cartTotalPrice);
      if (metadata.cartItemCount !== null) {
        this.dataset.lastCartItemCount = String(metadata.cartItemCount);
      }
      return true;
    }

    renderCount(count, cartTotalPrice) {
      var quantity = Math.max(0, safeNumber(count));
      var totalPrice = Math.max(0, safeNumber(cartTotalPrice));
      var result = evaluateMixedProgress(
        quantity,
        totalPrice,
        this.tiers,
        this.freeShippingThreshold,
        this.freeShippingNodeCount,
        {
          addAmountFreeShipping: this.dataset.addAmountFreeShippingTemplate,
          freeShippingThenDiscount: this.dataset.freeShippingThenDiscountTemplate,
          unlockDiscount: this.dataset.unlockDiscountTemplate,
          discountThenNext: this.dataset.discountThenNextTemplate,
          discountUnlocked: this.dataset.discountUnlockedTemplate
        }
      );
      if (!result) return;

      var max = result.maxCount;
      this.style.setProperty('--progress', Math.min(100, result.progressPct).toFixed(2) + '%');

      if (this.freeShippingMilestone) {
        this.freeShippingMilestone.style.left = result.freeShippingNodePct.toFixed(2) + '%';
        this.freeShippingMilestone.classList.toggle('is-earned', result.freeShippingReached);
      }
      if (this.freeShippingLabel) {
        this.freeShippingLabel.style.left = result.freeShippingNodePct.toFixed(2) + '%';
        this.freeShippingLabel.classList.toggle('is-earned', result.freeShippingReached);
      }

      this.milestones.forEach(function (m) {
        var tCount = safeNumber(m.dataset.count);
        m.style.left = Math.min(100, (tCount / max) * 100).toFixed(2) + '%';
        m.classList.toggle('is-earned', quantity >= tCount);
      });
      this.tierLabels.forEach(function (label) {
        var tCount = safeNumber(label.dataset.count);
        label.style.left = Math.min(100, (tCount / max) * 100).toFixed(2) + '%';
        label.classList.toggle('is-earned', quantity >= tCount);
      });

      if (this.messageEl) {
        this.messageEl.textContent = '';
        if (result.statusState === 'progress') {
          var parts = splitProgressMessage(result.message);
          if (parts.lead) {
            this.messageEl.appendChild(document.createTextNode(parts.lead));
          }
          var emphasis = document.createElement('span');
          emphasis.className = 'volume-discount-bar__message-emphasis';
          emphasis.textContent = parts.emphasis;
          this.messageEl.appendChild(emphasis);
        } else {
          this.messageEl.textContent = result.message;
        }
      }

      if (this.countEl) {
        this.countEl.textContent = (this.dataset.itemsCountTemplate || '[count] / [total]')
          .replace(/\[count\]/g, String(quantity))
          .replace(/\[total\]/g, String(max));
      }

      this.setAttribute('aria-valuenow', String(Math.min(quantity, max)));
      this.setAttribute('aria-valuemax', String(max));
      this.setAttribute('aria-valuetext',
        result.message || (this.dataset.eligibleItemsTemplate || '[count] / [total]')
          .replace(/\[count\]/g, String(quantity))
          .replace(/\[total\]/g, String(max)));
      this.dataset.statusState = result.statusState;
      this.dataset.freeShippingReached = String(result.freeShippingReached);
      this.dataset.currentDiscountPercent = String(result.currentDiscountPercent || 0);
      this.dispatchEvent(new CustomEvent('volume-discount:progress-rendered', {
        bubbles: true,
        detail: {
          progress: this,
          eligibleCount: quantity,
          cartTotalPrice: totalPrice,
          currentDiscountPercent: result.currentDiscountPercent || 0,
          statusState: result.statusState,
          freeShippingReached: result.freeShippingReached
        }
      }));
    }

  }

  if (!customElements.get('volume-discount-progress')) {
    customElements.define('volume-discount-progress', VolumeDiscountProgress);
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }
    var currency = window.Shopify && window.Shopify.currency
      ? window.Shopify.currency.active
      : 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: currency,
        currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (_) {
      return (cents / 100).toFixed(2);
    }
  }

  class VolumeDiscountTeaser extends HTMLElement {
    connectedCallback() {
      this._globalBar = document.getElementById('volume-discount-global-progress') ||
        document.querySelector('.volume-discount-bar--global');
      this._teaserHovered = false;
      this._teaserFocused = false;
      this._priceEls = Array.from(
        this.querySelectorAll('[data-volume-discount-unit-price]')
      );
      this._freeShippingCountEl = this.querySelector('[data-volume-discount-free-shipping-count]');
      this._freeShippingThreshold = optionalMoney(this.dataset.freeShippingThreshold) || 0;
      this._onTeaserPointerEnter = () => {
        this._teaserHovered = true;
        this._syncGlobalExpansion();
      };
      this._onTeaserPointerLeave = () => {
        this._teaserHovered = false;
        this._syncGlobalExpansion();
      };
      this._onTeaserFocusIn = () => {
        this._teaserFocused = true;
        this._syncGlobalExpansion();
      };
      this._onTeaserFocusOut = (event) => {
        this._teaserFocused = !!(event.relatedTarget && this.contains(event.relatedTarget));
        this._syncGlobalExpansion();
      };
      this._onVariantChange = (event) => {
        var detail = event && event.detail;
        var variant = detail && detail.variant;
        if (!variant || !Number.isFinite(Number(variant.price))) return;

        var eventProductId = detail.product && detail.product.id;
        if (eventProductId && String(eventProductId) !== this.dataset.productId) return;

        if (!eventProductId) {
          var eventSection = event.target && event.target.closest
            ? event.target.closest('.shopify-section')
            : null;
          var ownSection = this.closest('.shopify-section');
          if (eventSection && ownSection && eventSection !== ownSection) return;
        }

        this.updatePrices(Number(variant.price));
        this.dataset.currentVariantId = String(variant.id || '');
        this.dataset.currentVariantPrice = String(variant.price);
        syncVolumeDiscountTeaserGroup(this, variant);
      };
      this.addEventListener('pointerenter', this._onTeaserPointerEnter);
      this.addEventListener('pointerleave', this._onTeaserPointerLeave);
      this.addEventListener('focusin', this._onTeaserFocusIn);
      this.addEventListener('focusout', this._onTeaserFocusOut);
      document.addEventListener('on:variant:change', this._onVariantChange);
      this.updatePrices(safeNumber(this.dataset.currentVariantPrice));
      syncVolumeDiscountTeaserGroup(this, {
        id: this.dataset.currentVariantId,
        price: this.dataset.currentVariantPrice
      });
    }

    disconnectedCallback() {
      this.removeEventListener('pointerenter', this._onTeaserPointerEnter);
      this.removeEventListener('pointerleave', this._onTeaserPointerLeave);
      this.removeEventListener('focusin', this._onTeaserFocusIn);
      this.removeEventListener('focusout', this._onTeaserFocusOut);
      document.removeEventListener('on:variant:change', this._onVariantChange);
      if (this._globalBar) this._globalBar.classList.remove('is-teaser-expanded');
    }

    _syncGlobalExpansion() {
      if (!this._globalBar) return;
      this._globalBar.classList.toggle(
        'is-teaser-expanded',
        this._teaserHovered || this._teaserFocused
      );
    }

    updatePrices(variantPrice) {
      if (!Number.isFinite(variantPrice) || variantPrice < 0) return;
      this._priceEls.forEach(function (el) {
        var percent = safeNumber(el.dataset.percent);
        var unitPrice = Math.ceil(variantPrice * (100 - percent) / 100);
        el.textContent = formatMoney(unitPrice);
      });
      if (this._freeShippingCountEl && this._freeShippingThreshold > 0 && variantPrice > 0) {
        this._freeShippingCountEl.textContent = String(Math.ceil(this._freeShippingThreshold / variantPrice));
      }
    }
  }

  if (!customElements.get('volume-discount-teaser')) {
    customElements.define('volume-discount-teaser', VolumeDiscountTeaser);
  }

  class VolumeDiscountPdpSummary extends HTMLElement {
    connectedCallback() {
      this._priceEl = this.querySelector('[data-volume-discount-pdp-price]');
      this._compareEl = this.querySelector('[data-volume-discount-pdp-compare]');
      this._priceWrapper = this.querySelector('.price');
      this._swatchButton = this.querySelector('[data-volume-discount-pdp-swatch]');
      this._swatchLabel = this.querySelector('[data-volume-discount-pdp-swatch-label]');
      this._progressEl = document.getElementById('volume-discount-global-progress');
      this._quantityInput = this.querySelector('[data-volume-discount-pdp-quantity]');
      this._quantityButtons = Array.from(this.querySelectorAll('.volume-discount-global__pdp-quantity .qty-input__btn'));
      this._addButton = this.querySelector('[data-volume-discount-pdp-add]');
      this._variantInput = this.querySelector('[data-volume-discount-pdp-variant]');

      this._onVariantChange = (event) => {
        var detail = event && event.detail;
        var variant = detail && detail.variant;
        if (!variant || !Number.isFinite(Number(variant.price))) return;

        var eventProductId = detail.product && detail.product.id;
        if (eventProductId && String(eventProductId) !== this.dataset.productId) return;

        if (!eventProductId) {
          var eventSection = event.target && event.target.closest
            ? event.target.closest('.shopify-section')
            : null;
          var ownSection = this.closest('.shopify-section');
          if (eventSection && ownSection && eventSection !== ownSection) return;
        }

        this.dataset.currentVariantId = String(variant.id || '');
        this.dataset.currentVariantPrice = String(variant.price);
        this.dataset.currentVariantCompareAtPrice = String(variant.compare_at_price || 0);
        this.dataset.currentVariantAvailable = String(variant.available !== false);
        if (detail.product) this._backInStockProductData = detail.product;
        this.syncStickyVariantInput();
        this.renderPrice();
        this.syncStoqVariant();
        this.updateColorSwatch(variant, event.target);
      };

      this._onProgressRendered = (event) => {
        var detail = event && event.detail ? event.detail : {};
        var sourceProgress = detail.progress || event.target;
        if (this._progressEl && sourceProgress && sourceProgress !== this._progressEl) return;

        this.dataset.currentDiscountPercent = String(detail.currentDiscountPercent || 0);
        if (detail.eligibleCount !== undefined) {
          this.dataset.cartEligibleCount = String(detail.eligibleCount || 0);
        }
        this.renderPrice();
      };

      document.addEventListener('on:variant:change', this._onVariantChange);
      this._onStoqLoaded = () => {
        this.observeStoqButton();
        this.syncStoqVariant();
      };
      window.addEventListener('stoq:loaded', this._onStoqLoaded);
      document.addEventListener('volume-discount:progress-rendered', this._onProgressRendered);
      this._onQuantityChange = () => this.syncQuantityToMainForm();
      this._onAddClick = (event) => this.handleAddClick(event);
      this._onSwatchClick = () => this.scrollToProductMedia();
      if (this._quantityInput) {
        this._quantityInput.addEventListener('change', this._onQuantityChange);
        this._quantityInput.addEventListener('input', this._onQuantityChange);
      }
      if (this._addButton) this._addButton.addEventListener('click', this._onAddClick, true);
      if (this._swatchButton) this._swatchButton.addEventListener('click', this._onSwatchClick);

      if (this._progressEl && this._progressEl.dataset.currentDiscountPercent !== undefined) {
        this.dataset.currentDiscountPercent = this._progressEl.dataset.currentDiscountPercent;
      }
      if (this._progressEl) {
        this.dataset.cartEligibleCount = String(
          safeNumber(
            this._progressEl.dataset.lastEligibleCount ||
            this._progressEl.dataset.initialCount
          )
        );
      }
      this.syncQuantityFromMainForm();
      if (!this.syncFromMainVariant()) {
        this.renderPrice();
        this.updateColorSwatch(null, document.querySelector('.product-main variant-picker'));
        this.syncStoqVariant();
      }

      // The main PDP price is the only price source for this bar. Watching it
      // covers Shopify variant writes and asynchronous Regios writes without
      // reproducing either pricing calculation here.
      this._startObservingMainPrice();
      this.observeStoqButton();
    }

    // The picker can initialize before this element is defined. Read its
    // current selection so the sticky bar does not depend on catching an event.
    syncFromMainVariant() {
      var picker = document.querySelector('.product-main variant-picker');
      if (!picker) return false;
      var product = (picker.data && picker.data.product) || this.getBackInStockProductData();
      if (!product || String(product.id) !== this.dataset.productId) return false;

      var variant = picker.variant;
      if (!variant) {
        var form = this.getMainProductForm();
        var idInput = form && form.querySelector('[name="id"]');
        variant = idInput && Array.isArray(product.variants)
          ? product.variants.find(function (item) { return String(item.id) === String(idInput.value); })
          : null;
      }
      if (!variant) return false;

      this._onVariantChange({ target: picker, detail: { product: product, variant: variant } });
      return true;
    }

    /**
     * Set up a MutationObserver on the main product price container. On any
     * subtree mutation (including a replacement by DOPP), or any class change
     * on the price wrapper, copy the latest value into this bar.
     */
    _startObservingMainPrice() {
      if (this._mainPriceObserver) return;
      var self = this;
      var install = function () {
        var mainPriceEl = self.getMainProductPriceEl();
        if (!mainPriceEl) return false;
        var mainPriceContainer = mainPriceEl.closest('.product-info__price') || mainPriceEl;
        if (self._mainPriceObserver) {
          try { self._mainPriceObserver.disconnect(); } catch (e) { /* noop */ }
        }
        var pending = null;
        var schedule = function () {
          if (pending) return;
          pending = window.setTimeout(function () {
            pending = null;
            self.renderPrice();
          }, 0);
        };
        self._mainPriceObserver = new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            var m = mutations[i];
            if (m.type === 'attributes' && m.attributeName === 'class') { schedule(); break; }
            if (m.type === 'characterData' || m.type === 'childList' || m.type === 'subtree') { schedule(); break; }
          }
        });
        // Observe the stable container so replacing the inner .price element
        // cannot disconnect the sticky bar from its single price source.
        self._mainPriceObserver.observe(mainPriceContainer, {
          attributes: true,
          attributeFilter: ['class'],
          childList: true,
          characterData: true,
          subtree: true
        });
        // Also run once immediately to catch whatever the main price shows now.
        self.renderPrice();
        return true;
      };
      // Try to install synchronously, then poll briefly for late-arriving main price
      // (in case the main-product section renders after this global bar).
      if (!install()) {
        var attempts = 0;
        var retry = window.setInterval(function () {
          attempts++;
          if (install() || attempts > 40) {  // ~4s at 100ms
            window.clearInterval(retry);
          }
        }, 100);
        self._mainPriceInstallRetry = retry;
      }
    }

    disconnectedCallback() {
      if (this._stoqButtonObserver) {
        this._stoqButtonObserver.disconnect();
        this._stoqButtonObserver = null;
      }
      if (this._mainPriceObserver) {
        try { this._mainPriceObserver.disconnect(); } catch (e) { /* noop */ }
        this._mainPriceObserver = null;
      }
      if (this._mainPriceInstallRetry) {
        window.clearInterval(this._mainPriceInstallRetry);
        this._mainPriceInstallRetry = null;
      }
      document.removeEventListener('on:variant:change', this._onVariantChange);
      window.removeEventListener('stoq:loaded', this._onStoqLoaded);
      document.removeEventListener('volume-discount:progress-rendered', this._onProgressRendered);
      if (this._quantityInput) {
        this._quantityInput.removeEventListener('change', this._onQuantityChange);
        this._quantityInput.removeEventListener('input', this._onQuantityChange);
      }
      if (this._addButton) this._addButton.removeEventListener('click', this._onAddClick, true);
      if (this._swatchButton) this._swatchButton.removeEventListener('click', this._onSwatchClick);
    }

    getActiveColorSwatchLabel(source) {
      var picker = source && source.matches && source.matches('variant-picker')
        ? source
        : source && source.closest
          ? source.closest('variant-picker')
          : null;
      if (!picker) {
        picker = document.querySelector('.product-main variant-picker');
      }
      if (!picker) return null;
      return picker.querySelector('.option-selector[data-option-kind="color"] input.js-option:checked + .opt-label--swatch');
    }

    updateColorSwatch(variant, source) {
      if (!this._swatchButton) return;

      var label = this.getActiveColorSwatchLabel(source);
      var colorValue = '';
      var customImage = '';
      var customHex = '';

      if (label) {
        colorValue = label.querySelector('.js-value') ?
          label.querySelector('.js-value').textContent.trim() :
          (label.getAttribute('title') || '');
        customImage = label.style.getPropertyValue('--custom-swatch-image') ||
          label.style.getPropertyValue('--swatch-tooltip-image');
        customHex = label.style.getPropertyValue('--custom-swatch-hex');
      }

      if (!colorValue && variant && Array.isArray(variant.options)) {
        var colorSelector = document.querySelector('.product-main variant-picker .option-selector[data-option-kind="color"]');
        var colorIndex = colorSelector ? Number(colorSelector.dataset.optionIndex) : NaN;
        if (Number.isFinite(colorIndex) && variant.options[colorIndex]) {
          colorValue = variant.options[colorIndex];
        }
      }

      ['--custom-swatch-image', '--swatch-tooltip-image', '--custom-swatch-hex'].forEach((prop) => {
        this._swatchButton.style.removeProperty(prop);
      });

      if (customImage) {
        this._swatchButton.style.setProperty('--custom-swatch-image', customImage.trim());
      }
      if (customHex) {
        this._swatchButton.style.setProperty('--custom-swatch-hex', customHex.trim());
      }

      if (colorValue) {
        this._swatchButton.classList.remove('volume-discount-global__pdp-swatch--back-to-top');
        this._swatchButton.setAttribute('data-swatch', colorValue.replace(/"/g, '').toLowerCase());
        this._swatchButton.setAttribute('aria-label', colorValue);
        if (this._swatchLabel) this._swatchLabel.textContent = colorValue;
      } else {
        this._swatchButton.classList.add('volume-discount-global__pdp-swatch--back-to-top');
        this._swatchButton.removeAttribute('data-swatch');
        this._swatchButton.setAttribute('aria-label', this.dataset.backToTopText);
        if (this._swatchLabel) this._swatchLabel.textContent = this.dataset.backToTopText;
      }
    }

    isDesktopProductLayout() {
      if (window.theme && window.theme.mediaMatches && typeof window.theme.mediaMatches.md === 'boolean') {
        return window.theme.mediaMatches.md;
      }
      return window.matchMedia('(min-width: 769px)').matches;
    }

    resetMainProductColumns() {
      [
        document.querySelector('.product-main .product-media-column'),
        document.querySelector('.product-main .product-info')
      ].forEach(function (column) {
        if (column && column.scrollHeight > column.clientHeight) {
          column.scrollTo({ top: 0, behavior: 'smooth' });
        }
      });
    }

    scrollToProductMedia() {
      var isDesktop = this.isDesktopProductLayout();
      var target = isDesktop
        ? document.querySelector('.cc-main-product.product-main') || document.querySelector('.product-main')
        : document.querySelector('#product-media .media-gallery__viewer') ||
          document.querySelector('#product-media .media-gallery') ||
          document.getElementById('product-media') ||
          document.querySelector('.product-main .product-media');
      if (!target) return;

      var rootStyle = getComputedStyle(document.documentElement);
      var headerOffset = parseFloat(rootStyle.getPropertyValue('--header-end-padded')) ||
        parseFloat(rootStyle.getPropertyValue('--header-end')) ||
        0;
      var extraOffset = isDesktop ? 0 : 8;
      var top = Math.max(0, window.scrollY + target.getBoundingClientRect().top - headerOffset - extraOffset);

      window.scrollTo({ top: top, behavior: 'smooth' });
      if (isDesktop) this.resetMainProductColumns();
    }

    renderPrice() {
      if (!this._priceEl) {
        this.updateButtonState();
        return;
      }

      // PD-2026-09-03: Always mirror the main product price into this sticky
      // bar's price slot, regardless of whether the main price is rendered
      // by Shopify's native (variant.price / variant.compare_at_price) or by
      // the Regios DOPP app block. The bar lives in layout/theme.liquid so it
      // is outside the main-product section; the only way to keep it in
      // sync with whatever the main price is showing is to copy it. Falls
      // back to the variant.price formatter if the main price element isn't
      // available yet (e.g. before main-product.liquid has rendered).
      var mainPriceEl = this.getMainProductPriceEl();
      if (mainPriceEl) {
        var mainCurrent = mainPriceEl.querySelector('.price__current');
        if (mainCurrent) {
          this._priceEl.textContent = mainCurrent.textContent;
        }
        if (this._priceWrapper) {
          this._priceWrapper.classList.toggle(
            'price--on-sale',
            mainPriceEl.classList.contains('price--on-sale')
          );
        }
      } else {
        var variantPrice = Math.max(0, safeNumber(this.dataset.currentVariantPrice));
        this._priceEl.textContent = formatMoney(variantPrice);
      }

      if (this._compareEl) {
        this._compareEl.hidden = true;
        this._compareEl.textContent = '';
      }

      this.updateButtonState();
    }

    /**
     * PD-2026-09-03: Look up the main product price element. The bar lives in
     * layout/theme.liquid (global), so we can't just `this.closest('section')`;
     * instead search the whole document for `.product-info__price > .price`.
     */
    getMainProductPriceEl() {
      return document.querySelector('.product-info__price > .price');
    }

    updateButtonState() {
      // STOQ owns eligibility, market restrictions, limits and CTA text. Mirror
      // its real product-form button instead of guessing from inventory_policy.
      var purchase = this.getStoqPurchaseState();
      var preorderButton = purchase && !purchase.preorder ? null : this.getStoqPreorderButton();
      var isPreorder = purchase ? purchase.preorder : Boolean(preorderButton);
      var unavailable = this.dataset.currentVariantAvailable === 'false';
      var missingVariant = !this.dataset.currentVariantId;
      var preorderDisabled = purchase && (purchase.blocked || (isPreorder && !purchase.ready));
      preorderDisabled = preorderDisabled || (preorderButton && (preorderButton.disabled || preorderButton.getAttribute('aria-disabled') === 'true'));
      var quantityDisabled = missingVariant || (isPreorder ? Boolean(preorderDisabled) : unavailable);
      if (this._quantityInput) this._quantityInput.disabled = quantityDisabled;
      if (this._quantityButtons) {
        this._quantityButtons.forEach(function (button) {
          button.disabled = quantityDisabled;
        });
      }
      if (this._addButton) {
        this._addButton.classList.toggle('volume-discount-global__pdp-button--preorder', isPreorder);
      }
      // If STOQ selected the sticky form itself, leave its markup and state alone.
      if (preorderButton === this._addButton) return;
      if (this._addButton) {
        this._addButton.disabled = missingVariant || Boolean(preorderDisabled);
        // Mirror STOQ's configured/localized text without rewriting it. While
        // its native button is being rebuilt, retain the label and stay disabled.
        var label = preorderButton ? preorderButton.textContent.trim() : isPreorder ? this._addButton.textContent : unavailable
          ? (this._addButton.dataset.notifyText || this.dataset.restockNotifyText)
          : this._addButton.dataset.addText;
        if (this._addButton.textContent !== label) this._addButton.textContent = label;
        this._addButton.classList.toggle('volume-discount-global__pdp-button--notify', !isPreorder && unavailable);
        this._addButton.setAttribute(
          'aria-label',
          isPreorder ? label : unavailable ? this.dataset.notifyAvailableText : this._addButton.dataset.addText
        );
      }
    }

    syncStickyVariantInput() {
      if (!this._variantInput || this._variantInput.value === this.dataset.currentVariantId) return;
      this._variantInput.value = this.dataset.currentVariantId;
      this._variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    getStoqPreorderButton() {
      var form = this.getMainProductForm();
      return (form && form.querySelector('.restock-rocket-preorder-button')) ||
        this.querySelector('.restock-rocket-preorder-button');
    }

    getStoqPurchaseState() {
      var stoq = this.getStoqApi();
      var config = window._RestockRocketConfig;
      if (!stoq || !config || !config.settings || typeof stoq.shouldUpdateAddToCartButton !== 'function') return null;
      // STOQ's inventory ID lists use strict numeric comparisons. DOM datasets
      // are strings, so normalize every ID passed to the app's purchase APIs.
      var id = Number(this.dataset.currentVariantId);
      var preorder = stoq.shouldUpdateAddToCartButton(config.settings, id);
      var blocked = typeof stoq.shouldDisableForMarket === 'function' && stoq.shouldDisableForMarket(id);
      var button = preorder && this.getStoqPreorderButton();
      var form = button && button.closest('form');
      var input = form && form.querySelector('[name="id"]');
      var ready = Boolean(form && input && Number(input.value) === id &&
        Number(config.selected_variant_id) === id && form.classList.contains('restock-rocket-form-updated'));
      return { preorder: Boolean(preorder), blocked: Boolean(blocked), ready: ready };
    }

    observeStoqButton() {
      if (this._stoqButtonObserver || typeof MutationObserver === 'undefined') return;
      var form = this.getMainProductForm();
      if (!form) return;
      this._stoqButtonObserver = new MutationObserver(() => this.updateButtonState());
      this._stoqButtonObserver.observe(form, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'disabled', 'aria-disabled', 'value']
      });
      this.updateButtonState();
    }

    getMainProductForm() {
      return document.querySelector('.js-product-form-main');
    }

    sanitizeQuantity(value) {
      var quantity = Math.floor(safeNumber(value));
      return quantity > 0 ? quantity : 1;
    }

    getSelectedQuantity() {
      if (!this._quantityInput) return 1;
      return this.sanitizeQuantity(this._quantityInput.value);
    }

    handleAddClick(event) {
      var purchase = this.getStoqPurchaseState();
      if (purchase && (purchase.blocked || (purchase.preorder && !purchase.ready))) {
        if (event) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        this.syncStoqVariant();
        this.updateButtonState();
        return;
      }
      var preorderButton = purchase && !purchase.preorder ? null : this.getStoqPreorderButton();
      // A native click lets STOQ open its payment/terms modal and populate the
      // selling plan and line properties. requestSubmit() skips click handlers.
      if (preorderButton === this._addButton) return;
      // Templates without a main buy-buttons block can use the sticky form.
      if (!this.getMainProductForm() && this.dataset.currentVariantAvailable !== 'false') return;
      if (event && typeof event.preventDefault === 'function') event.preventDefault();

      if (preorderButton) {
        if (preorderButton.disabled || preorderButton.getAttribute('aria-disabled') === 'true') return;
        this.syncQuantityToMainForm();
        preorderButton.click();
        return;
      }

      if (this.dataset.currentVariantAvailable === 'false') {
        this.openBackInStockNotification();
        return;
      }

      if (this.isDesktopProductLayout()) {
        this.submitMainProductForm();
        return;
      }

      if (!this.openQuickAddDrawer()) {
        this.submitMainProductForm();
      }
    }

    getBackInStockProductData() {
      if (this._backInStockProductData !== undefined) return this._backInStockProductData;

      var dataEl = document.querySelector('.product-main variant-picker [type="application/json"]') ||
        document.querySelector('variant-picker [type="application/json"]');
      if (!dataEl) {
        this._backInStockProductData = null;
        return null;
      }

      try {
        var parsed = JSON.parse(dataEl.textContent);
        this._backInStockProductData = parsed && (parsed.product || parsed);
      } catch (e) {
        this._backInStockProductData = null;
      }

      return this._backInStockProductData;
    }

    getBackInStockVariantId() {
      return this.dataset.currentVariantId || '';
    }

    getStoqApi() {
      return window._RestockRocket || window.RestockRocket || window.restockRocket || null;
    }

    syncStoqVariant() {
      var stoq = this.getStoqApi();
      var variantId = this.getBackInStockVariantId();
      if (!stoq || !variantId || typeof stoq.renderButtonForVariant !== 'function') return;

      try {
        stoq.renderButtonForVariant(Number(variantId));
        this.updateButtonState();
      } catch (e) {
        /* Stoq is optional in local theme previews. */
      }
    }

    findStoqBackInStockButton() {
      var buttons = Array.from(document.querySelectorAll(
        '.restock-rocket-button:not(.restock-rocket-preorder-button), .restock-rocket-button-container button:not(.restock-rocket-preorder-button)'
      ));
      return buttons.find(function (button) {
        return button.offsetParent !== null;
      }) || buttons[0] || null;
    }

    clickStoqBackInStockButton() {
      var button = this.findStoqBackInStockButton();
      if (!button) return false;
      button.click();
      return true;
    }

    openStoqBackInStockModal() {
      var stoq = this.getStoqApi();
      if (!stoq || typeof stoq.openModal !== 'function') {
        return this.clickStoqBackInStockButton();
      }

      var productData = this.getBackInStockProductData();
      var variantId = this.getBackInStockVariantId();

      try {
        if (productData && variantId) {
          stoq.openModal(productData, variantId);
        } else {
          stoq.openModal();
        }
        return true;
      } catch (e) {
        try {
          stoq.openModal();
          return true;
        } catch (fallbackError) {
          return this.clickStoqBackInStockButton();
        }
      }
    }

    waitForStoqAndOpen() {
      var didOpen = false;
      var timeoutId = null;
      var open = () => {
        if (didOpen) return;
        if (this.openStoqBackInStockModal()) {
          didOpen = true;
          cleanup();
        }
      };
      var cleanup = () => {
        window.removeEventListener('stoq:loaded', open);
        document.removeEventListener('stoq:loaded', open);
        if (timeoutId) window.clearTimeout(timeoutId);
      };

      window.addEventListener('stoq:loaded', open, { once: true });
      document.addEventListener('stoq:loaded', open, { once: true });
      timeoutId = window.setTimeout(() => {
        if (!didOpen) {
          cleanup();
          if (!this.clickStoqBackInStockButton()) this.scrollToProductOptions();
        }
      }, 1200);
    }

    scrollToProductOptions() {
      var target = document.getElementById('variants') ||
        document.getElementById('product-info') ||
        document.querySelector('.product-main variant-picker');
      if (target && typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    openBackInStockNotification() {
      if (this.openStoqBackInStockModal()) {
        return;
      }

      this.waitForStoqAndOpen();
    }

    getQuickAddProductUrl() {
      var productUrl = this.dataset.productUrl || window.location.pathname;
      var variantId = this.dataset.currentVariantId;

      try {
        var url = new URL(productUrl, window.location.origin);
        if (variantId) url.searchParams.set('variant', variantId);
        return /^https?:\/\//i.test(productUrl)
          ? url.toString()
          : url.pathname + url.search + url.hash;
      } catch (e) {
        if (!variantId) return productUrl;
        return productUrl.indexOf('?') === -1
          ? productUrl + '?variant=' + variantId
          : productUrl + '&variant=' + variantId;
      }
    }

    openQuickAddDrawer() {
      var drawer = document.querySelector('quick-add-drawer');
      if (!drawer || typeof drawer.open !== 'function' || !this._addButton) return false;

      this._addButton.dataset.productUrl = this.getQuickAddProductUrl();
      if (this.dataset.currentVariantId) {
        this._addButton.dataset.variantId = this.dataset.currentVariantId;
      } else {
        delete this._addButton.dataset.variantId;
      }

      drawer.open(this._addButton);
      return true;
    }

    getProjectedDiscountPercent() {
      var cartCount = safeNumber(this.dataset.cartEligibleCount);
      var projectedCount = cartCount + this.getSelectedQuantity();
      var tiers = this.getDiscountTiers();
      var discountPercent = 0;

      tiers.forEach(function (tier) {
        if (projectedCount >= tier.count) {
          discountPercent = Math.max(discountPercent, tier.percent);
        }
      });

      return discountPercent;
    }

    getDiscountTiers() {
      if (!this._progressEl) return [];
      return [1, 2, 3, 4].map(function (index) {
        return {
          enabled: this._progressEl.getAttribute('data-tier-' + index + '-enabled') === 'true',
          count: safeNumber(this._progressEl.getAttribute('data-tier-' + index + '-count')),
          percent: safeNumber(this._progressEl.getAttribute('data-tier-' + index + '-percent'))
        };
      }, this).filter(function (tier) {
        return tier.enabled && tier.count > 0 && tier.percent > 0;
      }).sort(function (a, b) {
        return a.count - b.count;
      });
    }

    syncQuantityFromMainForm() {
      if (!this._quantityInput) return;
      var form = this.getMainProductForm();
      var formQuantity = form && form.querySelector('[name="quantity"]');
      if (!formQuantity || formQuantity.value === '') return;
      this._quantityInput.value = String(this.sanitizeQuantity(formQuantity.value));
    }

    syncQuantityToMainForm() {
      if (!this._quantityInput) return;
      var form = this.getMainProductForm();
      if (!form) return;
      var formQuantity = form.querySelector('[name="quantity"]');
      if (!formQuantity) {
        formQuantity = document.createElement('input');
        formQuantity.type = 'hidden';
        formQuantity.name = 'quantity';
        form.appendChild(formQuantity);
      }
      var quantity = this.sanitizeQuantity(this._quantityInput.value);
      this._quantityInput.value = String(quantity);
      formQuantity.value = String(quantity);
      formQuantity.dispatchEvent(new Event('change', { bubbles: true }));
      this.renderPrice();
    }

    submitMainProductForm() {
      var form = document.querySelector('.js-product-form-main');
      if (!form) {
        var anchor = document.getElementById('product-info');
        if (anchor && typeof anchor.scrollIntoView === 'function') {
          anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      var idInput = form.querySelector('[name="id"]');
      if (idInput) idInput.disabled = false;
      this.syncQuantityToMainForm();

      var submitButton = form.querySelector('[name="add"]');
      if (submitButton && typeof form.requestSubmit === 'function') {
        form.requestSubmit(submitButton);
      } else if (submitButton) {
        submitButton.click();
      } else {
        form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      }
    }
  }

  if (!customElements.get('volume-discount-pdp-summary')) {
    customElements.define('volume-discount-pdp-summary', VolumeDiscountPdpSummary);
  }

  setupGlobalProgressSuppression();

  // Diagnostic / manual API.
  var previousApi = window.PolymakerVolumeDiscount;
  if (previousApi && typeof previousApi.destroy === 'function') {
    try { previousApi.destroy(); } catch (_) { /* never let stale state block */ }
  }

  function getProgressElements() {
    return Array.from(document.querySelectorAll('volume-discount-progress'));
  }

  window.PolymakerVolumeDiscount = {
    refresh: function (source) {
      var jobs = getProgressElements().map(function (el) {
        if (!el || typeof el.refresh !== 'function') return Promise.resolve(false);
        return Promise.resolve(el.refresh(source || 'manual'));
      });
      return Promise.all(jobs).then(function () {
        return window.PolymakerVolumeDiscount.debug();
      });
    },
    debug: function () {
      var elements = getProgressElements();
      var globalElement = document.getElementById('volume-discount-global-progress') ||
        document.querySelector('.volume-discount-bar--global');
      var messageElement = globalElement &&
        globalElement.querySelector('.volume-discount-bar__message');
      var registeredClass = customElements.get('volume-discount-progress');
      return {
        version: IMPLEMENTATION_VERSION,
        instanceCount: elements.length,
        customElementClass: registeredClass ? registeredClass.name : null,
        elementImplementation: globalElement
          ? globalElement.dataset.volumeDiscountImplementation || null
          : null,
        needsReload: !!globalElement &&
          globalElement.dataset.volumeDiscountImplementation !== IMPLEMENTATION_VERSION,
        allCartLinesEligible: globalElement
          ? globalElement.dataset.allCartLinesEligible === 'true' ||
            globalElement.dataset.allProductsEligible === 'true'
          : null,
        eligibleCollectionHandles: globalElement && globalElement.eligibleCollectionHandles
          ? globalElement.eligibleCollectionHandles.slice()
          : null,
        tiers: globalElement && globalElement.tiers
          ? globalElement.tiers.map(function (tier) {
            return {
              enabled: tier.enabled,
              count: tier.count,
              percent: tier.percent
            };
          })
          : null,
        freeShippingThreshold: globalElement ? safeNumber(globalElement.dataset.freeShippingThreshold) : null,
        freeShippingReached: globalElement ? globalElement.dataset.freeShippingReached === 'true' : null,
        cartTotalPrice: globalElement ? safeNumber(globalElement.dataset.lastCartTotalPrice || globalElement.dataset.cartTotalPrice) : null,
        globalValue: globalElement ? globalElement.getAttribute('aria-valuenow') : null,
        globalText: messageElement ? messageElement.textContent.trim() : null,
        lastSync: globalElement && globalElement._lastSync
          ? Object.assign({}, globalElement._lastSync)
          : null
      };
    },
    destroy: function () {
      // No global state to tear down.
    }
  };

  window.VolumeDiscountEvaluate = evaluateTiers;
  window.VolumeDiscountEvaluateMixed = evaluateMixedProgress;
})();
